import { forwardRef } from "react";

const LOGO = "https://customer-assets.emergentagent.com/job_e31510f3-0985-434c-b5ba-05b91fac6a7c/artifacts/ywyzdsaz_logomarca.jpeg";

/**
 * Cinematic shareable card for Instagram / social — 1080x1350 (4:5).
 * Pure CSS, captured to PNG via html-to-image.
 *
 * variant: "certificate" | "completion"
 */
const ShareCard = forwardRef(function ShareCard(
    { variant = "certificate", userName, journeyTitle, journeyCover, hours, moduleTitle },
    ref
) {
    const isCert = variant === "certificate";

    return (
        <div
            ref={ref}
            data-testid="share-card"
            style={{
                width: 1080,
                height: 1350,
                position: "relative",
                overflow: "hidden",
                background:
                    "radial-gradient(circle at 20% 10%, rgba(0,212,212,0.20), transparent 45%), radial-gradient(circle at 85% 90%, rgba(212,175,55,0.18), transparent 45%), radial-gradient(circle at 50% 50%, rgba(147,112,219,0.12), transparent 60%), #050507",
                fontFamily: "'Manrope', sans-serif",
                color: "#FCFCFB",
                padding: 80,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            {/* Background journey cover (subtle) */}
            {journeyCover && (
                <img
                    src={journeyCover}
                    alt=""
                    crossOrigin="anonymous"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.18,
                        filter: "blur(2px) saturate(140%)",
                    }}
                />
            )}

            {/* Top overlay gradients */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(5,5,7,0.65) 0%, rgba(5,5,7,0.35) 40%, rgba(5,5,7,0.85) 100%)",
                }}
            />

            {/* Holographic top arc */}
            <div
                style={{
                    position: "absolute",
                    top: -300,
                    left: -100,
                    width: 1300,
                    height: 600,
                    borderRadius: "50%",
                    background:
                        "conic-gradient(from 180deg at 50% 50%, rgba(0,122,255,0.0), rgba(0,212,212,0.35), rgba(147,112,219,0.35), rgba(212,175,55,0.35), rgba(0,122,255,0.0))",
                    filter: "blur(60px)",
                    opacity: 0.6,
                }}
            />

            {/* Content layer */}
            <div style={{ position: "relative", zIndex: 2 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 20,
                    }}
                >
                    <div
                        style={{
                            width: 84,
                            height: 84,
                            borderRadius: 22,
                            overflow: "hidden",
                            background: "#000",
                            boxShadow: "0 0 40px rgba(0,212,212,0.35)",
                        }}
                    >
                        <img
                            src={LOGO}
                            alt="IMQ"
                            crossOrigin="anonymous"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    </div>
                    <div>
                        <div
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: 16,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.7)",
                            }}
                        >
                            Instituto de
                        </div>
                        <div
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: 32,
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                marginTop: 2,
                            }}
                        >
                            Medicina Quântica
                        </div>
                    </div>
                </div>
            </div>

            {/* Center hero text */}
            <div style={{ position: "relative", zIndex: 2 }}>
                <div
                    style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 18,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.6)",
                    }}
                >
                    {isCert ? "Certificado · Jornada concluída" : "Módulo concluído"}
                </div>

                <div
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 72,
                        lineHeight: 1.02,
                        fontWeight: 400,
                        marginTop: 32,
                        background:
                            "linear-gradient(120deg, #007AFF 0%, #00D4D4 30%, #9370DB 65%, #D4AF37 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        maxWidth: 900,
                    }}
                >
                    {isCert
                        ? `Eu expandi minha consciência.`
                        : `Mais um passo de consciência.`}
                </div>

                <div
                    style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 300,
                        fontSize: 56,
                        letterSpacing: "-0.02em",
                        marginTop: 64,
                        color: "#FCFCFB",
                    }}
                >
                    {userName}
                </div>
                <div
                    style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 24,
                        color: "rgba(255,255,255,0.65)",
                        marginTop: 14,
                    }}
                >
                    {isCert ? "concluiu a jornada" : "completou o módulo"}
                </div>
                <div
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 44,
                        marginTop: 6,
                        color: "#FCFCFB",
                        maxWidth: 900,
                        lineHeight: 1.15,
                    }}
                >
                    {isCert ? journeyTitle : moduleTitle}
                </div>
                {isCert && journeyTitle && hours != null && (
                    <div
                        style={{
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: 18,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.55)",
                            marginTop: 24,
                        }}
                    >
                        {hours} horas de imersão
                    </div>
                )}
                {!isCert && journeyTitle && (
                    <div
                        style={{
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: 18,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.55)",
                            marginTop: 24,
                        }}
                    >
                        Jornada · {journeyTitle}
                    </div>
                )}
            </div>

            {/* Bottom strip */}
            <div
                style={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: 28,
                    borderTop: "1px solid rgba(212,175,55,0.35)",
                }}
            >
                <div
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 26,
                        color: "rgba(255,255,255,0.75)",
                    }}
                >
                    Onde a consciência encontra a ciência.
                </div>
                <div
                    style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 16,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.55)",
                    }}
                >
                    medicinaquantica.com
                </div>
            </div>
        </div>
    );
});

export default ShareCard;
